import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { getPaginationItems } from "@/lib/pagination";

import { API_URL_DEPOSIT_DETAILS } from "../../../../actions";
import client from "../../../../client";
import { OFFER_TYPES } from "../../../../constants";
import { formatNumber } from "../../../../utils";
import { deliveryCostAmount, effectiveDeliveryCost } from "../../../../utils/delivery-cost";
import { UserGroup } from "../../../../utils/roles";
import { Aggregator } from "../../../AggregatorDetailsModal/types";
import { AddressHolder, DeliveryLookups, NamedEntity } from "../../types";
import {
  closingAcceptsDeliveryCost,
  demandLinesInPlay,
  groupOffersByDemand,
  OFFERS_PAGE_SIZE,
} from "../../utils";
import { DemandGroupCard } from "../DemandGroupCard";
import { DeliveryCostNegotiationModal } from "../DeliveryCostNegotiationModal";
import { CounterOfferModal, ExchangeLocation } from "../CounterOfferModal";
import type { CounterOfferFormValues } from "../CounterOfferModal/schema";
import { OfferCard, offerCurrencySymbol } from "../OfferCard";
import { Offer, OfferStatus } from "../OfferCard/interfaces";
import { OfferCommentsModal } from "../OfferCommentsModal";
import {
  makeProposal,
  NegotiationProposal,
} from "../OfferCommentsModal/NegotiatedDetailsCard/offer-negotiation-proposal";
import { ConnectedRecallOfferModal, RecallFormValues } from "../RecallOfferModal";
import { SendingBackExchangeModal, SendingBackFormValues } from "../SendingBackExchangeModal";

// One dialog is open at a time, and each carries exactly the context it needs.
type OfferDialog =
  | { kind: "comments"; offer: Offer }
  | { kind: "recall"; offer: Offer; fromComments?: boolean }
  | { kind: "sendBack"; offer: Offer }
  | { kind: "counterOffer"; offer: Offer; proposal: CounterOfferFormValues }
  | { kind: "deliveryCost"; demandId: number }
  | null;

interface OffersListProps {
  offers: Offer[];
  totalItems: number;
  page: number;
  onPageChange: (page: number) => void;
  traders: AddressHolder[];
  trader: AddressHolder;
  aggregators: Aggregator[];
  customExchangeLocations: ExchangeLocation[];
  exchangeLocationTypes: NamedEntity[];
  administrativeAreas: NamedEntity[];
  readOnly?: boolean;
  refreshList: () => void;
  refreshContent: (offer: Offer) => void;
  onValidateRecall: (offer: Offer, values: RecallFormValues) => Promise<unknown>;
  onValidateSendingBack: (offer: Offer, values: SendingBackFormValues) => Promise<unknown>;
  addCounterOffer: (
    offer: Offer,
    values: CounterOfferFormValues
  ) => Promise<Partial<NegotiationProposal>>;
}

export const OffersList = ({
  offers,
  totalItems,
  page,
  onPageChange,
  traders,
  trader,
  aggregators,
  customExchangeLocations,
  exchangeLocationTypes,
  administrativeAreas,
  readOnly = false,
  refreshList,
  refreshContent,
  onValidateRecall,
  onValidateSendingBack,
  addCounterOffer,
}: OffersListProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [dialog, setDialog] = useState<OfferDialog>(null);
  // Set by in-modal actions that change an offer without refetching the list
  // (accept, send-back), so we only reload on close when something actually changed.
  const [listStale, setListStale] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalItems / OFFERS_PAGE_SIZE));

  const openAggregatorDetails = (id: number) =>
    history.push(
      readOnly ? `/wfpManager/aggregators/${id}` : `/representative/market/aggregator/${id}`
    );

  const rows = useMemo(() => groupOffersByDemand(offers), [offers]);

  const deliveryLookups: DeliveryLookups = useMemo(
    () => ({
      traders: traders || [],
      aggregators: aggregators || [],
      customExchangeLocations: customExchangeLocations || [],
      exchangeLocationTypes: exchangeLocationTypes || [],
      administrativeAreas: administrativeAreas || [],
    }),
    [traders, aggregators, customExchangeLocations, exchangeLocationTypes, administrativeAreas]
  );

  const closeDialog = () => {
    setDialog(null);
    setListStale(false);
  };

  // The comments modal mounts on open, so every path into it refetches the thread.
  // This is what the legacy `triggerReset` uuid did via componentDidUpdate.
  const openComments = (offer: Offer) => {
    setDialog({ kind: "comments", offer });
    refreshContent(offer);
  };

  const handleCounterOffer = async (proposal: NegotiationProposal) => {
    if (dialog?.kind !== "comments") {
      return;
    }
    const offer = dialog.offer;
    const aggregator = aggregators.find(item => item.id === offer.tagged_aggregator.id);
    const initialValues = makeProposal(
      { ...proposal, delivery_cost: effectiveDeliveryCost(proposal, offer) },
      trader,
      aggregator,
      t,
      customExchangeLocations,
      exchangeLocationTypes,
      administrativeAreas
    );

    setDialog({ kind: "counterOffer", offer, proposal: initialValues });

    // The list payload carries a partial deposit; the form validates against its full quantity.
    if (offer.offer_type === OFFER_TYPES.DIRECT_OFFER && offer.deposit?.uuid) {
      const { data } = await client.get(`${API_URL_DEPOSIT_DETAILS}${offer.deposit.uuid}/`);
      setDialog(current =>
        current?.kind === "counterOffer" && current.offer.id === offer.id
          ? { ...current, offer: { ...current.offer, deposit: data } }
          : current
      );
    }
  };

  const openDeliveryCost = (demandId: number) => setDialog({ kind: "deliveryCost", demandId });

  // Recalling a demand's last standing line can lock the demand's cost on the aggregator's pending
  // proposal. That happens for sure when another line of the demand is in play; when none is at
  // hand (other lines may be on another page), the warning says it depends on them.
  const recallWarning = (offer: Offer): string | undefined => {
    const amount = closingAcceptsDeliveryCost(offer);
    if (amount === null || amount === undefined) {
      return undefined;
    }
    const siblingInPlay =
      demandLinesInPlay(offers.filter(line => line.demand === offer.demand && line.id !== offer.id))
        .length > 0;
    return t(
      siblingInPlay
        ? "deliveryCostNegotiation.lastLineWarning"
        : "deliveryCostNegotiation.lastLineRecallWarning",
      {
        amount: `${offerCurrencySymbol(t)}${formatNumber(deliveryCostAmount(amount))}`,
      }
    );
  };

  const handleSubmitCounterOffer = async (offer: Offer, values: CounterOfferFormValues) => {
    const proposal = await addCounterOffer(offer, values);
    refreshList();

    // Show the new proposal in the thread straight away, before the thread refetch lands.
    openComments({
      ...offer,
      latest_negotiation_proposal: {
        ...proposal,
        user: {
          id: offer.trader_representative.id,
          first_name: offer.trader_representative.person.first_name,
          last_name: offer.trader_representative.person.last_name,
          roles: [UserGroup.TRADER_REPR],
        },
      } as NegotiationProposal,
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {rows.map(row => {
        if (row.type === "demand" && row.offers.length > 1) {
          return (
            <DemandGroupCard
              key={`demand-${row.demandId}`}
              demandId={row.demandId}
              offers={row.offers}
              onViewComments={openComments}
              onRecall={o => setDialog({ kind: "recall", offer: o })}
              onAggregatorDetails={openAggregatorDetails}
              onReviewDeliveryCost={readOnly ? undefined : openDeliveryCost}
              deliveryLookups={deliveryLookups}
              hideRecall={readOnly}
              showBuyer={readOnly}
            />
          );
        }
        // A demand with a single line reads as a plain offer, so it renders as one.
        // A school offer drops its grade and cannot be recalled (the bid flow governs it).
        const offer = row.type === "demand" ? row.offers[0] : row.offer;
        return (
          <OfferCard
            key={row.type === "demand" ? `demand-${row.demandId}` : offer.id}
            offer={offer}
            onViewComments={openComments}
            onRecall={o => setDialog({ kind: "recall", offer: o })}
            onAggregatorDetails={openAggregatorDetails}
            onReviewDeliveryCost={readOnly ? undefined : openDeliveryCost}
            deliveryLookups={deliveryLookups}
            hideQuality={!!offer.is_school_offer}
            hideRecall={readOnly || !!offer.is_school_offer}
            showBuyer={readOnly}
          />
        );
      })}

      {totalPages > 1 && (
        <Pagination className="pt-2">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => page > 1 && onPageChange(page - 1)}
                disabled={page <= 1}
              />
            </PaginationItem>
            {getPaginationItems(page, totalPages).map((item, index) =>
              item === "ellipsis" ? (
                <PaginationItem key={`ellipsis-${index}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={item}>
                  <PaginationLink isActive={item === page} onClick={() => onPageChange(item)}>
                    {item}
                  </PaginationLink>
                </PaginationItem>
              )
            )}
            <PaginationItem>
              <PaginationNext
                onClick={() => page < totalPages && onPageChange(page + 1)}
                disabled={page >= totalPages}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <ConnectedRecallOfferModal
        isOpen={dialog?.kind === "recall"}
        offer={dialog?.kind === "recall" ? dialog.offer : undefined}
        warning={dialog?.kind === "recall" ? recallWarning(dialog.offer) : undefined}
        onCancel={() =>
          dialog?.kind === "recall" && dialog.fromComments
            ? setDialog({ kind: "comments", offer: dialog.offer })
            : closeDialog()
        }
        onValidateRecall={async (offer, values) => {
          await onValidateRecall(offer, values);
          closeDialog();
        }}
      />

      <SendingBackExchangeModal
        isOpen={dialog?.kind === "sendBack"}
        offer={dialog?.kind === "sendBack" ? dialog.offer : undefined}
        onCancel={closeDialog}
        onValidateSendingBack={async (offer, values) => {
          await onValidateSendingBack(offer, values);
          setListStale(true);
          // Reopen the thread on the disputed offer, as the aggregator now sees it.
          openComments({ ...offer, status: OfferStatus.ExchangeDetailsDisputed });
        }}
      />

      {dialog?.kind === "comments" && (
        <OfferCommentsModal
          offer={dialog.offer}
          deliveryLookups={deliveryLookups}
          readOnly={readOnly}
          setSeeReasonsOffer={(offer: Offer) => {
            setListStale(true);
            setDialog({ kind: "comments", offer });
          }}
          onCancel={() => {
            if (listStale) {
              refreshList();
            }
            closeDialog();
          }}
          onRecallOffer={async () =>
            setDialog({ kind: "recall", offer: dialog.offer, fromComments: true })
          }
          onSendBackExchange={async () => setDialog({ kind: "sendBack", offer: dialog.offer })}
          onCounterOffer={handleCounterOffer}
        />
      )}

      {dialog?.kind === "counterOffer" && (
        <CounterOfferModal
          key={`${dialog.offer.id}:${dialog.offer.deposit?.quantity ?? ""}`}
          administrativeAreas={administrativeAreas}
          trader={trader}
          aggregators={aggregators}
          customExchangeLocations={customExchangeLocations}
          exchangeLocationTypes={exchangeLocationTypes}
          offer={dialog.offer}
          initialValues={dialog.proposal}
          onValidate={handleSubmitCounterOffer}
          onCancel={() => setDialog({ kind: "comments", offer: dialog.offer })}
        />
      )}

      {dialog?.kind === "deliveryCost" && (
        <DeliveryCostNegotiationModal
          demandId={dialog.demandId}
          // An accepted or countered cost is done: close and show the demand's new state.
          onChanged={() => {
            closeDialog();
            refreshList();
          }}
          onClose={closeDialog}
        />
      )}
    </div>
  );
};
